import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import type { SalesCategorySizes } from "api-types/builders/salesCategoryTypes";
import type { ClientLocationPlanWithOverrides } from "api-types/locationPlans/locationPlansResponses";
import type { ClientLocationWithLevel } from "api-types/locations/locationBaseTypes";
import type { ClientLotCommunityPlanHydrated } from "api-types/lotCommunityPlans/lotCommunityPlansResponses";
import type { ClientLot } from "api-types/lots/lotsResponses";
import type { ClientMasterPlanHydrated } from "api-types/masterPlans/masterPlansResponses";
import type { Doc } from "building-types/doc";
import type { DocOptionRule } from "building-types/options/docOptionRule";
import type { DocOptionEffect, OptionEffectOverrides } from "building-types/options/optionEffect";
import type { DocOption, DocOptionSet } from "building-types/options/optionSet";
import type { OptionSetEntityRelations } from "building-types/options/optionSetEntityRelationsTypes";
import type {
  OptionOverrides,
  OptionSetOverride,
  OptionSetOverrides,
} from "building-types/options/optionTemplateTypes";
import type { Table } from "common/data/table";
import type { OptionRuleReferencesByOption, OptionRuleReferencesByOptionSet } from "doc/options/optionRule";

export interface PlanLibraryOptionSet extends Omit<DocOptionSet, "options"> {
  isBooleanOptionSet: boolean;
  isFilteredBySearch: boolean;
  ruleRefsByOption: OptionRuleReferencesByOption;
  options: DocOption[];
}

export type PlanLibraryOptionSets = {
  [optionSetId: string]: PlanLibraryOptionSet;
};

export type PlanLibraryOptionSetsPerSalesCategory = {
  [salesCategoryName: string]: PlanLibraryOptionSet[];
};

export interface PlanLibraryOptionRuleData {
  ruleIds: string[];
  optionSetId?: string;
  optionKey?: string;
}

interface PlanLibraryMasterPlan {
  planType: "master_plan";
  masterPlan: ClientMasterPlanHydrated;
}

interface PlanLibraryLocationPlan {
  planType: "community_plan";
  masterPlan: ClientMasterPlanHydrated;
  locationPlan: ClientLocationPlanWithOverrides;
  location: ClientLocationWithLevel;
}

interface PlanLibraryLot {
  planType: "lot";
  masterPlan: ClientMasterPlanHydrated;
  locationPlan: ClientLocationPlanWithOverrides;
  location: ClientLocationWithLevel;
  lotCommunityPlan: ClientLotCommunityPlanHydrated;
  lot: ClientLot;
}

export type PlanLibraryPlans = PlanLibraryMasterPlan | PlanLibraryLocationPlan | PlanLibraryLot;

export enum PlanLibraryStateType {
  UNINITIALIZED,
  INITIALIZED,
}

export interface PlanLibraryDefaultState {
  state: PlanLibraryStateType.UNINITIALIZED;
}

export interface PlanLibraryInitializedState {
  state: PlanLibraryStateType.INITIALIZED;
  builder: ClientBuilder;

  optionSetEntityRelations: OptionSetEntityRelations;
  inheritedOptionSets: PlanLibraryOptionSets; // inheritedOptionSets and unchangedOptionSets are for all intents and purposes
  unchangedOptionSets: Doc["optionSets"]; // identical other than in how the data is structured and organized
  overrides: OptionSetOverrides;

  originalOptionRules: Table<DocOptionRule>;
  originalOptionEffects: Table<DocOptionEffect>;
  optionEffectOverrides: OptionEffectOverrides;
  optionRuleRefsByOptionSets: OptionRuleReferencesByOptionSet;

  salesCategorySizes: SalesCategorySizes; // key: secondary level sales category, can include special categories
  salesCategoryNames: string[]; // secondary level sales category, also keys to salesCategorySizes
  primarySalesCategorySizes: SalesCategorySizes; // key: primary level key values, can include special categories
  primarySalesCategoryNames: string[]; // primary level key values, also keys to primarySalesCategorySizes
  primarySalesCategoryKeyToNameMap: Record<string, string>; // mapping from primary level key to pretty name (if it exists, uses key if it doesn't)
  secondaryToPrimarySalesCategoryMap: Record<string, string>; // mapping from secondary level sales category strings to primary level key values
  elevationOptionSet: PlanLibraryOptionSet | null;
  booleanOptionSetsPerSalesCategory: PlanLibraryOptionSetsPerSalesCategory; // key: secondary level sales category, can include special categories
  nonBoolOptionSetsPerSalesCategory: PlanLibraryOptionSetsPerSalesCategory; // key: secondary level sales category, can include special categories
  hideEmptySalesCategories: boolean;
  searchFor: string;

  overridesToDiscard?: OptionSetOverride | OptionOverrides; // activates discard summary in <PlanLibraryModalWrapper><Body>
  onHandleDiscarding?: () => void; // called when modal discard button pressed
  modalOptionSet?: PlanLibraryOptionSet;
  modalOption?: DocOption;
  editOptionRules?: PlanLibraryOptionRuleData;

  discardAllOverrides: boolean;
  needsSaving: boolean;
  massEditing: boolean;
  isReadOnly: boolean;

  planName: string | null | undefined;
  planId: string;

  plans: PlanLibraryPlans;
}

export type PlanLibraryState = PlanLibraryInitializedState | PlanLibraryDefaultState;

export type PlanLibraryEvent = "config:discarded_all_overrides_for_plan";
